import { createContext, useContext } from 'react';

export type DeviceTypeState =
  | {
      isDesktopDevice: true;
      isTabletDevice: false;
      isMobileDevice: false;
    }
  | {
      isDesktopDevice: false;
      isTabletDevice: true;
      isMobileDevice: false;
    }
  | {
      isDesktopDevice: false;
      isTabletDevice: false;
      isMobileDevice: true;
    };

export type DynamicDeviceTypeState = {
  isLargeDesktop: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  isXsMobile: boolean;
};

/**
 * Our best determination of what the device type and window size are for the
 * device. We are _always_ better off using CSS to make decisions about how to
 * present pages / UI elements because that presentation will be consistent between
 * service side rendered pages and client side renders.  We get inconstancy, which
 * can lead to errors when a) we render for desktop on the server and b) we detect
 * mobile on the client and render different DOM elements (or dom attributes).
 *
 * @field isTabletDevice - based on the the user agent, the device is a tablet
 * @field isMobileDevice - based on the user agent, the device is mobile
 * @field isDesktopDevice - true if we could not determine from the user agent that the device is mobile or tablet
 * @field isLargeDesktop - Based on the window size, is this a large desktop
 * @field isDesktop - Based on the window size, is this a desktop of any size
 * @field isTablet - Based on the window size, is this a tablet
 * @field isMobile - Based on the window size, is this a mobile device
 * @field isXsMobile - Based on the window , is this a very small mobile device
 */
export type ResponsiveDesignState = DeviceTypeState & DynamicDeviceTypeState;

export const ResponsiveDesignContext = createContext<ResponsiveDesignState>({
  isDesktopDevice: false,
  isTabletDevice: false,
  isMobileDevice: true,
  isLargeDesktop: false,
  isDesktop: false,
  isTablet: false,
  isMobile: false,
  isXsMobile: false,
});

/**
 * Our best determination of what the device type and window size are for the
 * device. We are _always_ better off using CSS to make decisions about how to
 * present pages / UI elements because that presentation will be consistent between
 * service side rendered pages and client side renders.  We get inconstancy, which
 * can lead to errors when a) we render for desktop on the server and b) we detect
 * mobile on the client and render different DOM elements (or dom attributes).
 *
 * NOTE: If you are using next, you are best off only using the device type
 * methods in the component hierarchy.  Using the window based properties will
 * likely result in different DOM structures or attributes between the server
 * and client. Any device specific changes should be isolated in CSS as much as
 * possible.
 *
 * @returns ResponsiveDesignState
 *
 * @see ResponsiveDesignState
 *
 * @example
 * // Based on the user agent, is this not a tablet and not mobile device
 * const { isDesktopDevice } = useResponsiveDesign();
 *
 * @example
 * // Based on the user agent, is this a tablet
 * const { isTabletDevice } = useResponsiveDesign();
 *
 * @example
 * // Based on the user agent, is this a mobile device
 * const { isMobileDevice } = useResponsiveDesign();
 *
 * @example
 * // Based on the window size, is this a large desktop
 * const { isLargeDesktop } = useResponsiveDesign();
 *
 * @example
 * // Based on the window size, or the user agent if this is used on the server, is this not a tablet and not a mobile device
 * const { isDesktop } = useResponsiveDesign();
 *
 * @example
 * // Based on the window size, or the user agent if this is used on the server, is this a tablet
 * const { isTablet } = useResponsiveDesign();
 *
 * @example
 * // Based on the window size, or the user agent if this is used on the server, is this a mobile device
 * const { isMobile } = useResponsiveDesign();
 *
 * @example
 * // Based on the window size, is this an xs mobile device
 * const { isXsMobile } = useResponsiveDesign();
 *
 */
export const useResponsiveDesign = (): ResponsiveDesignState => useContext(ResponsiveDesignContext);
